import { ZonedDateTime } from '@internationalized/date';
import { RoutePlanType } from '@/types/route';
import { AssignmentCandidateLocation, BaseAssignmentCandidate } from '@/api/apis/resources';
import { AirportRouteLocation, CustomLocationType } from '@/api/apis/route';
import { RouteTypeSettingsEnum, StartLocationObjectType, TravelTypeHosTx } from '@/api/apis/mission-planner';

export enum SuggestedSettingsEnum {
  RouteType = 'routeType',
  OR = 'or',
  CS = 'cs',
  RS = 'rs',
  StartLocation = 'startLocation',
  StartTime = 'startTime',
  DeploymentHub = 'deploymentHub',
  ArrivalHub = 'arrivalHub',
  OrganTransportationType = 'organTransportationType',
}

type EmployeeResourceValue<T> = Pick<
  BaseAssignmentCandidate,
  'employeeId' | 'employeeFullName' | 'employeeProfilePhoto'
> &
  T;

export type SettingsValueType<T extends SuggestedSettingsEnum, V> = {
  type: T;
  value: V;
};

// SuggestedSettingsEnum.RouteType
export type RouteTypeSettingsType = SettingsValueType<SuggestedSettingsEnum.RouteType, RouteTypeSettingsEnum>;

// SuggestedSettingsEnum.CS
type CSResourceValue = EmployeeResourceValue<{
  hubId: string;
  hubCode: string;
}>;

export type CSSettingsType = SettingsValueType<SuggestedSettingsEnum.CS, CSResourceValue | null>;

// SuggestedSettingsEnum.RS
type RSResourceValue = EmployeeResourceValue<{
  location?: AssignmentCandidateLocation;
}>;

export type RSSettingsType = SettingsValueType<SuggestedSettingsEnum.RS, RSResourceValue | null>;

// SuggestedSettingsEnum.DeploymentHub
export type DeploymentHubSettingsType = SettingsValueType<SuggestedSettingsEnum.DeploymentHub, string | null>;

// SuggestedSettingsEnum.ArrivalHub
export type ArrivalHubSettingsType = SettingsValueType<SuggestedSettingsEnum.ArrivalHub, string | null>;

// SuggestedSettingsEnum.OR
export enum OrSettingsEnum {
  CurrentOr = 'currentOr',
  OrRange = 'orRange',
  CustomOr = 'customOr',
}

export type OrSettingsDataType = {
  type: OrSettingsEnum;
  offset: number;
  selectedOr: ZonedDateTime;
};

export type OrSettingsType = SettingsValueType<SuggestedSettingsEnum.OR, OrSettingsDataType>;

// SuggestedSettingsEnum.StartLocation
type GenericStartLocationType<T> = {
  type: StartLocationObjectType;
  id: string;
  location: CustomLocationType;
} & T;

type HubStartLocationType = GenericStartLocationType<{
  type: StartLocationObjectType.Hub;
  code: string;
}>;

type DHStartLocationType = GenericStartLocationType<{
  type: StartLocationObjectType.DH;
  code: string;
}>;

type TXCStartLocationType = GenericStartLocationType<{
  type: StartLocationObjectType.TXC;
  code: string;
}>;

export type AirportStartLocationType = GenericStartLocationType<
  {
    type: StartLocationObjectType.Airport;
  } & AirportRouteLocation
>;

export type CustomStartLocationType = GenericStartLocationType<{
  type: StartLocationObjectType.Custom;
  customTitle: string;
}>;

export type StartLocationType =
  | HubStartLocationType
  | DHStartLocationType
  | TXCStartLocationType
  | AirportStartLocationType
  | CustomStartLocationType;

export type StartLocationSettingsType = SettingsValueType<
  SuggestedSettingsEnum.StartLocation,
  StartLocationType | null
>;

// SuggestedSettingsEnum.OrganTransportationType
export type OrganTransportationTypeSettingsType = SettingsValueType<
  SuggestedSettingsEnum.OrganTransportationType,
  TravelTypeHosTx
>;

// SuggestedSettingsEnum.StartTime
export type StartTimeSettingsType = SettingsValueType<SuggestedSettingsEnum.StartTime, ZonedDateTime>;

export type SuggestedSettingsType =
  | RouteTypeSettingsType
  | CSSettingsType
  | RSSettingsType
  | DeploymentHubSettingsType
  | ArrivalHubSettingsType
  | OrSettingsType
  | StartLocationSettingsType
  | StartTimeSettingsType
  | OrganTransportationTypeSettingsType;

export type SuggestedSettingsKeyValueType = {
  [K in SuggestedSettingsType['type']]: Extract<SuggestedSettingsType, { type: K }>['value'];
};

export type GeneratedRouteType = {
  id: string;
  index: number;
  orTimeUTC: string;
  orTimeDiffMs: number;
  routes: RoutePlanType[];
};

export type GeneratedPlansMap = Record<string, GeneratedRouteType>;

export type SurgeonRoutePlan = Pick<RoutePlanType, 'segments' | 'legs' | 'locations' | 'resources'>;

export type CommercialRoutePlan = Pick<RoutePlanType, 'segments' | 'legs' | 'locations'>;

export type SurgeonPlansMap = Record<string, (SurgeonRoutePlan | null)[]>;

export type CommercialPlansMap = Record<string, CommercialRoutePlan[]>;

export type SuggestedPlansData = {
  taskId: string;
  planType: RouteTypeSettingsEnum;
  plans: GeneratedPlansMap;
  forcedSurgeonId?: string;
  /** Set when a newer generation replaced this one, so its caller must leave the new one alone. */
  aborted?: boolean;
};
